import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import TeamLeadFilter from "@/components/TeamLeadFilter";
import { useAuth } from "@/contexts/AuthContext";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from "recharts";

const COLORS = ["#F26B3A", "#1E88E5", "#f59e0b", "#3b82f6", "#8b5cf6", "#ef4444"];
const TICK = { fontSize: 11 };
const BAR_RADIUS = [4, 4, 0, 0];
const LEGEND_STYLE = { fontSize: 10 };

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tlFilter, setTlFilter] = useState("_all_");
  const canFilter = ["super_admin", "hr", "manager", "ceo"].includes(user?.role);

  useEffect(() => {
    const url = tlFilter && tlFilter !== "_all_"
      ? `/reports/summary?team_lead_id=${encodeURIComponent(tlFilter)}`
      : "/reports/summary";
    api.get(url).then((r) => setData(r.data));
  }, [tlFilter]);

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reports & Analytics"
        subtitle="Quick read-outs across the people lifecycle."
        action={canFilter ? (
          <TeamLeadFilter value={tlFilter} onChange={setTlFilter} testIdPrefix="reports-tl-filter" />
        ) : null}
      />

      {tlFilter && tlFilter !== "_all_" && (
        <div className="mb-4 text-xs px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-900" data-testid="reports-scoped-banner">
          Showing data for the selected Team Lead only.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="hr-card p-5">
          <h2 className="font-display text-lg font-semibold mb-3">Headcount by department</h2>
          <div className="h-64">
            {data && (
              <ResponsiveContainer>
                <BarChart data={data.headcount_by_dept}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="name" tick={TICK} stroke="#6b7280" />
                  <YAxis tick={TICK} stroke="#6b7280" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#1E88E5" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="hr-card p-5">
          <h2 className="font-display text-lg font-semibold mb-3">Candidates by stage</h2>
          <div className="h-64">
            {data && (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.candidates_by_stage} dataKey="value" nameKey="name" outerRadius={80}>
                    {data.candidates_by_stage.map((entry) => <Cell key={`r-${entry.name}`} fill={COLORS[data.candidates_by_stage.indexOf(entry) % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="hr-card p-5">
          <h2 className="font-display text-lg font-semibold mb-3">Leaves by status</h2>
          <div className="h-64">
            {data && (
              <ResponsiveContainer>
                <BarChart data={data.leaves_by_status}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="name" tick={TICK} stroke="#6b7280" />
                  <YAxis tick={TICK} stroke="#6b7280" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#F26B3A" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="hr-card p-5">
          <h2 className="font-display text-lg font-semibold mb-3">Monthly payout (current)</h2>
          <div className="hr-kpi text-5xl mt-6">₹ {data ? data.payroll_total.toLocaleString("en-IN") : "—"}</div>
          <div className="text-xs text-gray-500 mt-2">Net payroll for the active cycle.</div>
        </Card>
      </div>
    </div>
  );
}
